import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, NgClass, NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { cookiesStorage } from '@jsverse/transloco-persist-lang';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { ToastModule } from 'primeng/toast';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { buildRoutes } from '../../../app.routes';
import { ApprezzoCommonModule } from '../../../apprezzoo-common.module';
import { AuthService } from '../../../services/auth/auth.service';
import { DialogService } from '../../../services/dialog-service.service';
import { PagesService } from '../../../services/pages/pages.service';
import { LoginSignupComponent } from '../../public/login-signup/login-signup.component';

@Component({
  selector: 'app-header',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    TranslocoDirective,
    DialogModule,
    LoginSignupComponent,
    SkeletonModule,
    MenuModule,
    ToastModule,
    NgIf,
    ApprezzoCommonModule,
    NgClass,
    ButtonModule,
    StyleClassModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() isPrivate: boolean = false;

  private readonly destroy$ = new Subject<void>();

  pagesService = inject(PagesService);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  authService = inject(AuthService);
  dialogService = inject(DialogService);
  breakpointObserver = inject(BreakpointObserver);
  items$: Observable<MenuItem[]> = this.pagesService.getPages();
  userMenuitems$: Observable<MenuItem[]> = this.pagesService.getUserMenu();
  isLoggedIn$: Observable<boolean> = this.authService.isLoggedIn$;
  isVisible = false;
  isLoading = true;
  currentLang: string = '';
  currentUrl = '';
  menuVisible = true;
  isMobile = false;
  isMenuButtonAvailable = true;
  platform;
  @ViewChild('menuButton', { static: false }) menuButton!: ElementRef;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.platform = Capacitor.getPlatform();
  }

  ngOnInit(): void {
    this.dialogService.hideDialog();
    this.dialogService.dialogVisibility$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => (this.isVisible = visible));
    this.currentLang = this.translocoService.getActiveLang();
    this.currentUrl = this.router.url;

    this.isLoggedIn$.pipe(take(1)).subscribe((loggedIn) => {
      if (!loggedIn && this.platform !== 'web') {
        this.isMenuButtonAvailable = false;
      }
      this.isLoading = false;
    });
    this.breakpointObserver.observe('(max-width: 991px)').subscribe((result) => {
      this.isMobile = result.matches;
      if (this.isMobile) this.closeMenu();
    });
    this.pagesService.menuVisible$.subscribe((visible) => {
      this.menuVisible = visible;
    });
  }
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.menuVisible &&
      this.menuButton &&
      !this.menuButton.nativeElement.contains(event.target)
    ) {
      this.closeMenu();
    }
  }

  onToggleClick(): void {
    if (this.menuVisible) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.pagesService.setMenuVisible(true);
    document.body.classList.add('no-scroll');
  }

  closeMenu() {
    this.pagesService.setMenuVisible(false);
    document.body.classList.remove('no-scroll');
  }

  async changeLanguage(lang: string) {
    try {
      this.currentUrl = this.router.url;

      this.currentLang = lang;
      this.translocoService.setActiveLang(lang);
      cookiesStorage().setItem('translocoLang', lang);

      const newRoutes = await buildRoutes(this.translocoService);
      this.router.resetConfig(newRoutes);
      const newUrl = await this.getTranslatedUrl(this.currentUrl);

      this.document.documentElement.lang = this.translocoService.getActiveLang();

      if (!newUrl.includes('new-password')) {
        setTimeout(() => {
          this.router.navigate([newUrl]);
        }, 100);
      }
    } catch (error) {
      //  console.error('Errore durante il cambio lingua o la traduzione:', error);
    }
  }

  private async getTranslatedUrl(url: string): Promise<string> {
    if (!url || url === '/') return '/';

    const segmentUrls = url.split('/').filter((segment) => segment.trim() !== '');

    const lastSegment =
      segmentUrls.length > 0 && !isNaN(Number(segmentUrls[segmentUrls.length - 1]))
        ? segmentUrls.pop()
        : null;

    const translations = await firstValueFrom(
      this.translocoService.selectTranslateObject('routes'),
    );

    const translatedSegments = segmentUrls.map((segment) => translations[segment] || segment);

    return '/' + translatedSegments.join('/') + (lastSegment ? `/${lastSegment}` : '');
  }

  navigateAndClose(url: string): void {
    this.closeMenu();
    this.pagesService.toggleMenu();

    setTimeout(() => {
      this.router.navigate([url]);
    }, 100);
  }

  isActive(url: string): boolean {
    const currentSegments = this.router.url.split('/').filter(Boolean);
    const targetSegments = url.split('/').filter(Boolean);

    return currentSegments[0] === targetSegments[0] && currentSegments[1] === targetSegments[1];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
